/*@jsxRuntime automatic @jsxImportSource react*/
import {jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {MainLayout} from '~/layouts/main';
import {Quiz} from '~/components/Quiz';
export const meta = {
  title: 'LSP | Введение',
  description: 'Введение в принцип подстановки Барбары Лисков, его смысл, основные понятия'
};
const MDXLayout = ({children}) => _jsx(MainLayout, {
  meta: meta,
  children: children
});
function _createMdxContent(props) {
  const _components = Object.assign({
    section: "section",
    h1: "h1",
    p: "p",
    em: "em",
    h2: "h2",
    code: "code",
    pre: "pre",
    a: "a",
    ul: "ul",
    li: "li"
  }, _provideComponents(), props.components);
  return _jsxs(_components.section, {
    children: [_jsx(_components.h1, {
      children: "Введение"
    }), _jsx(_components.p, {
      children: "Одна из частых ошибок проектирования программных систем — это попытка полностью скопировать иерархию объектов из реального мира."
    }), _jsxs(_components.p, {
      children: ["Моделируя систему, мы описываем ", _jsx(_components.em, {
        children: "поведение"
      }), " её компонентов, ", _jsx(_components.em, {
        children: "отношения"
      }), " их друг с другом, а не иерархию. Иерархия — удобный инструмент для моделирования, но иногда она приводит к неправильному описанию поведения."]
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "Классический пример"
      }), _jsxs(_components.p, {
        children: ["Представим, что есть класс ", _jsx(_components.code, {
          children: "Rectangle"
        }), ", который описывает прямоугольник:"]
      }), _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "language-ts",
          children: "class Rectangle {\n  width: number\n  height: number\n\n  constructor(width: number, height: number) {\n    this.width = width\n    this.height = height\n  }\n\n  setWidth(width: number) {\n    this.width = width\n  }\n\n  setHeight(height: number) {\n    this.height = height\n  }\n\n  areaOf(): number {\n    return this.width * this.height\n  }\n}\n"
        })
      }), _jsx(_components.p, {
        children: "Квадрат — тоже прямоугольник, мы можем использовать наследование, чтобы описать его:"
      }), _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "language-ts",
          children: "class Square extends Rectangle {\n  width: number\n  height: number\n\n  constructor(size: number) {\n    super(size, size)\n  }\n\n  setWidth(width: number) {\n    this.width = width\n    this.height = width\n  }\n\n  setHeight(height: number) {\n    this.width = height\n    this.height = height\n  }\n}\n"
        })
      }), _jsx(_components.p, {
        children: "Дальше в коде мы используем квадрат. Кажется, что всё в порядке:"
      }), _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "language-ts",
          children: "declare const square: Square\n\nsquare.setWidth(20) // меняет ширину и высоту, всё верно\nsquare.setHeight(40) // тоже меняет ширину и высоту, ок\n"
        })
      }), _jsxs(_components.p, {
        children: ["Но если мы используем класс ", _jsx(_components.code, {
          children: "Rectangle"
        }), " в качестве интерфейса, а работаем с конкретным классом ", _jsx(_components.code, {
          children: "Square"
        }), ", то могут возникнуть проблемы:"]
      }), _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "language-ts",
          children: "function testShapeSize(figure: Rectangle) {\n  figure.setWidth(10)\n  figure.setHeight(20)\n  assert(figure.areaOf() === 200)\n  // условие не сработает, если figure — экземпляр класса Square\n}\n"
        })
      }), _jsx(_components.p, {
        children: "Разница между квадратом и прямоугольником в том, что у квадрата при изменении стороны меняются обе стороны, у прямоугольника — только одна, вторая остаётся неизменной."
      }), _jsxs(_components.p, {
        children: ["Математически — да, квадрат всё ещё прямоугольник, но он ", _jsx(_components.em, {
          children: "ведёт себя иначе"
        }), ", чем прямоугольник."]
      })]
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "Принцип подстановки Барбары Лисков"
      }), _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "https://ru.wikipedia.org/wiki/%D0%9F%D1%80%D0%B8%D0%BD%D1%86%D0%B8%D0%BF_%D0%BF%D0%BE%D0%B4%D1%81%D1%82%D0%B0%D0%BD%D0%BE%D0%B2%D0%BA%D0%B8_%D0%91%D0%B0%D1%80%D0%B1%D0%B0%D1%80%D1%8B_%D0%9B%D0%B8%D1%81%D0%BA%D0%BE%D0%B2",
          children: "Принцип подстановки Барбары Лисков"
        }), " (Liskov Substitution Principle, LSP) решает эту проблему, вводя ограничения для иерархии объектов."]
      }), _jsx(_components.p, {
        children: "Звучит он так: функции, которые используют базовый тип, должны иметь возможность использовать подтипы базового типа, не зная об этом."
      }), _jsx(_components.p, {
        children: "Простыми словами — классы-наследники не должны противоречить базовому классу. Например, они не могут предоставлять интерфейс ýже базового. Поведение наследников должно быть ожидаемым для функций, которые используют базовый класс."
      }), _jsx(_components.p, {
        children: "Немного удобнее думать об LSP в терминах «абстракция — реализация». Абстрактный класс или интерфейс играют роль базового типа, но вместе с этим — роль контракта на поведение."
      }), _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "language-ts",
          children: "abstract class Disposable {\n  protected source: Optional<number>;\n  constructor(fn: AnyFunction, delay: TimeIntervalMS) {}\n\n  dispose(): void {}\n}\n"
        })
      }), _jsx(_components.p, {
        children: "Они гарантируют, что экземпляр любого конкретного класса будет содержать указанные поля и методы."
      }), _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "language-ts",
          children: "class Interval extends Disposable {\n  constructor(fn: AnyFunction, delay: Milliseconds) {\n    super(fn, delay);\n    this.source = setInterval(fn, delay);\n  }\n\n  dispose = () => clearInterval(this.source);\n}\n\nclass Timer extends Disposable {\n  constructor(fn: AnyFunction, delay: Milliseconds) {\n    super(fn, delay);\n    this.source = setTimeout(fn, delay);\n  }\n\n  dispose = () => clearTimeout(this.source);\n}\n"
        })
      }), _jsxs(_components.p, {
        children: ["Это значит, что модуль, использующий этот ", _jsx(_components.em, {
          children: "абстрактный класс или интерфейс"
        }), " сможет работать с ", _jsx(_components.em, {
          children: "любой"
        }), " его реализацией. Например, функции ", _jsx(_components.code, {
          children: "cleanup"
        }), " будет неважно, экземпляр какого конкретного класса мы передадим:"]
      }), _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "language-ts",
          children: "function cleanup(disposable: Disposable): void {\n  disposable.dispose();\n}\n\nconst interval = new Interval(() => console.log('Hey!'), 1000);\nconst timer = new Timer(() => alert('Hey!'), 1000);\n\ncleanup(interval);\ncleanup(timer);\n"
        })
      }), _jsxs(_components.p, {
        children: ["Принцип можно связать с понятиями ", _jsx(_components.a, {
          href: "https://ru.wikipedia.org/wiki/%D0%9A%D0%BE%D0%B2%D0%B0%D1%80%D0%B8%D0%B0%D0%BD%D1%82%D0%BD%D0%BE%D1%81%D1%82%D1%8C_%D0%B8_%D0%BA%D0%BE%D0%BD%D1%82%D1%80%D0%B0%D0%B2%D0%B0%D1%80%D0%B8%D0%B0%D0%BD%D1%82%D0%BD%D0%BE%D1%81%D1%82%D1%8C_(%D0%BF%D1%80%D0%BE%D0%B3%D1%80%D0%B0%D0%BC%D0%BC%D0%B8%D1%80%D0%BE%D0%B2%D0%B0%D0%BD%D0%B8%D0%B5)",
          children: "ковариантности и контравариантности"
        }), ", а также с  ", _jsx(_components.a, {
          href: "https://ru.wikipedia.org/wiki/%D0%9A%D0%BE%D0%BD%D1%82%D1%80%D0%B0%D0%BA%D1%82%D0%BD%D0%BE%D0%B5_%D0%BF%D1%80%D0%BE%D0%B3%D1%80%D0%B0%D0%BC%D0%BC%D0%B8%D1%80%D0%BE%D0%B2%D0%B0%D0%BD%D0%B8%D0%B5",
          children: "контрактным программированием"
        }), ". В частности полезны отношения предусловий и постусловий для базового и наследников:"]
      }), _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "предусловия не могут быть усилены в подклассе;"
        }), "\n", _jsx(_components.li, {
          children: "постусловия не могут быть ослаблены в подклассе."
        }), "\n"]
      })]
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "Снова пример"
      }), _jsxs(_components.p, {
        children: ["В примере с ", _jsx(_components.code, {
          children: "Rectangle"
        }), " и ", _jsx(_components.code, {
          children: "Square"
        }), " последний ослабляет постусловие для методов ", _jsx(_components.code, {
          children: "setWidth"
        }), " и ", _jsx(_components.code, {
          children: "setHeight"
        }), ". Разберём, что это за постусловие."]
      }), _jsxs(_components.p, {
        children: ["Если мы работаем с методом ", _jsx(_components.code, {
          children: "setHeight"
        }), " класса ", _jsx(_components.code, {
          children: "Rectangle"
        }), ", то после вызова метода будем наблюдать ситуацию, когда:"]
      }), _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "language-ts",
          children: "const oldHeight = figure.height\nfigure.setWidth(newWidth)\n\nassert((figure.width === newWidth) && (figure.height === oldHeight))\n"
        })
      }), _jsxs(_components.p, {
        children: ["Но в случае с квадратом это не так. Постусловие — ", _jsx(_components.em, {
          children: "свойства или состояние после выполнения метода"
        }), " — ослабляется:"]
      }), _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "language-ts",
          children: "const oldHeight = figure.height\nfigure.setWidth(newWidth)\n\n// постусловие ослаблено, абстракция неправильная\nassert((figure.width === newWidth))\n"
        })
      }), _jsxs(_components.p, {
        children: ["Из-за этого использовать ", _jsx(_components.code, {
          children: "Rectangle"
        }), " вместо ", _jsx(_components.code, {
          children: "Square"
        }), " без дополнительных проверок или изменения уже существующих компонентов невозможно."]
      }), _jsxs(_components.p, {
        children: ["Принцип подстановки Лисков требует использовать общий интерфейс для обоих классов и не наследовать ", _jsx(_components.code, {
          children: "Square"
        }), " от ", _jsx(_components.code, {
          children: "Rectangle"
        }), "."]
      }), _jsx(_components.p, {
        children: "Общий интерфейс должен быть таким, чтобы в классах, реализующих его, предусловия не были более сильными, а постусловия не были более слабыми."
      })]
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "Наследование и композиция"
      }), _jsxs(_components.p, {
        children: ["Следовать LSP проще, если не строить больших и глубоких иерархий. Обычно, для связи модулей ", _jsx(_components.em, {
          children: "достаточно интерфейса и его реализаций"
        }), "."]
      }), _jsxs(_components.p, {
        children: ["Длинные цепочки иерархий типов — хрупкие. Вместо иерархий типов лучше использовать ", _jsx(_components.a, {
          href: "/ocp",
          children: "композицию"
        }), ", чтобы собирать сущности из необходимой функциональности."]
      }), _jsx(_components.p, {
        children: "Например, наследование предполагает проектирование от общего к частному в виде иерархии:"
      }), _jsx(_components.pre, {
        children: _jsx(_components.code, {
          children: "Животные → Млекопитающие → Человек.\n"
        })
      }), _jsx(_components.p, {
        children: "Но такие иерархии не всегда правильно отражают связи сущностей в проектируемой системе. Иногда иерархий вовсе может быть несколько одновременно, тогда наследование зайдёт в тупик — непонятно по какой иерархии наследоваться и как."
      }), _jsxs(_components.p, {
        children: ["Композиция же подразумевает проектирование от частного к общему в виде ", _jsx(_components.a, {
          href: "/isp",
          children: "совокупности нескольких обособленных наборов «фич»"
        }), ":"]
      }), _jsx(_components.pre, {
        children: _jsx(_components.code, {
          children: "Человек = Скелет + Нервная система + Иммунная система + Сердечно-сосудистая система + ...\n"
        })
      }), _jsx(_components.p, {
        children: "Таким образом мы не ограничиваем себя рамками одной иерархии, а значит нам необязательно подводить каждый аспект бизнес-логики под эту иерархию."
      }), _jsx(_components.p, {
        children: "Более того, часто в отношениях сущностей, которые мы моделируем, в принципе нет никакой иерархии. В таком случае наследование сущностей наоборот лишь навредит."
      })]
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "Коротко"
      }), _jsx(_components.p, {
        children: "Принцип подстановки Барбары Лисков:"
      }), _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "помогает проектировать систему, опираясь на поведение модулей;"
        }), "\n", _jsx(_components.li, {
          children: "вводит ограничения и правила наследования объектов, чтобы их потомки не противоречили базовому поведению;"
        }), "\n", _jsx(_components.li, {
          children: "делает поведение модулей последовательным и предсказуемым;"
        }), "\n", _jsx(_components.li, {
          children: "помогает избегать дублирования, выделять общую для нескольких модулей функциональность в общий интерфейс;"
        }), "\n", _jsx(_components.li, {
          children: "позволяет выявлять при проектировании проблемные абстракции и скрытые связи между сущностями."
        }), "\n"]
      })]
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "Материалы к разделу"
      }), _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://ru.wikipedia.org/wiki/%D0%9F%D1%80%D0%B8%D0%BD%D1%86%D0%B8%D0%BF_%D0%BF%D0%BE%D0%B4%D1%81%D1%82%D0%B0%D0%BD%D0%BE%D0%B2%D0%BA%D0%B8_%D0%91%D0%B0%D1%80%D0%B1%D0%B0%D1%80%D1%8B_%D0%9B%D0%B8%D1%81%D0%BA%D0%BE%D0%B2",
            children: "Принцип подстановки Барбары Лисков"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://ru.wikipedia.org/wiki/%D0%9A%D0%BE%D0%BD%D1%82%D1%80%D0%B0%D0%BA%D1%82%D0%BD%D0%BE%D0%B5_%D0%BF%D1%80%D0%BE%D0%B3%D1%80%D0%B0%D0%BC%D0%BC%D0%B8%D1%80%D0%BE%D0%B2%D0%B0%D0%BD%D0%B8%D0%B5",
            children: "Контрактное программирование"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://ru.wikipedia.org/wiki/%D0%9A%D0%BE%D0%B2%D0%B0%D1%80%D0%B8%D0%B0%D0%BD%D1%82%D0%BD%D0%BE%D1%81%D1%82%D1%8C_%D0%B8_%D0%BA%D0%BE%D0%BD%D1%82%D1%80%D0%B0%D0%B2%D0%B0%D1%80%D0%B8%D0%B0%D0%BD%D1%82%D0%BD%D0%BE%D1%81%D1%82%D1%8C_(%D0%BF%D1%80%D0%BE%D0%B3%D1%80%D0%B0%D0%BC%D0%BC%D0%B8%D1%80%D0%BE%D0%B2%D0%B0%D0%BD%D0%B8%D0%B5)",
            children: "Ковариантность и контравариантность"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://blog.byndyu.ru/2009/10/blog-post_29.html",
            children: "Принцип замещения Лисков"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://stackoverflow.com/questions/56860/what-is-an-example-of-the-liskov-substitution-principle",
            children: "What is an example of the Liskov Substitution Principle?"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "http://www.labri.fr/perso/clement/enseignements/ao/LSP.pdf",
            children: "The Liskov Substitution Principle, PDF"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "http://se.ethz.ch/~meyer/publications/computer/contract.pdf",
            children: "Applying “Design by Contract”, Bertrand Meyer, PDF"
          })
        }), "\n"]
      })]
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "Вопросы"
      }), _jsx(Quiz, {
        name: "lsp-intro-1"
      }), _jsx(Quiz, {
        name: "lsp-intro-2"
      }), _jsx(Quiz, {
        name: "lsp-intro-3"
      })]
    })]
  });
}
function MDXContent(props = {}) {
  return _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, props)
  }));
}
export default MDXContent;
